import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";

const GDProfileAdd = () => {
  const Navigate = useNavigate();
  const token = localStorage.getItem("Token");

  const [user, setUser] = useState(null);
  const [updatedFields, setUpdatedFields] = useState({});
  const [isModified, setIsModified] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [profilePreview, setProfilePreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [profileFile, setProfileFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  // ✅ Fetch profile
  const getProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_URL}/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.data) {
        const profile = res.data.data;
        setUser(profile);
        localStorage.setItem("user", JSON.stringify(profile));
      }
    } catch (err) {
      if (err.response?.data?.message === "Unauthorized!") {
        Navigate("/");
      } else {
        toast.error("Failed to fetch profile");
      }
    }
  }, [token, Navigate]);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
    getProfile();
  }, [getProfile]);

  // ✅ Handle input changes
  const handleInputChange = (e, field) => {
    const { value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setUpdatedFields((prev) => ({ ...prev, [field]: newValue }));
    setUser((prev) => ({ ...prev, [field]: newValue }));
    setIsModified(true);
  };

  // ✅ Handle image upload
  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload a valid image file");
        return;
      }
      if (file.size / 1024 / 1024 > 2) {
        toast.error("Image size must be less than 2MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (type === "profile") {
          setProfilePreview(ev.target.result);
          setProfileFile(file);
        } else {
          setLogoPreview(ev.target.result);
          setLogoFile(file);
        }
      };
      reader.readAsDataURL(file);
      setIsModified(true);
    }
  };

  // ✅ Save / Update
  const handleSave = async () => {
    if (!isModified && !profileFile && !logoFile) {
      toast.info("No changes to save");
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();

      Object.keys(updatedFields).forEach((key) => {
        const value = updatedFields[key];
        if (value !== null && value !== undefined) formData.append(key, value);
      });

      if (profileFile) formData.append("profile_image", profileFile);
      if (logoFile) formData.append("logo_image", logoFile);

      const res = await axios.put(
        `${process.env.REACT_APP_URL}/auth/profile`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.status === 200) {
        toast.success("Profile updated successfully");
        await getProfile();
        setIsModified(false);
        setUpdatedFields({});
        setProfileFile(null);
        setLogoFile(null);
        setProfilePreview(null);
        setLogoPreview(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update profile");
    } finally {
      setUploading(false);
    }
  };

  if (!user)
    return (
      <div className="al">
        <div className="al-card" style={{ textAlign: "center", padding: "48px 20px", color: "var(--color-text-secondary)" }}>Loading...</div>
      </div>
    );

  return (
    <div className="al">
      <Toaster position="top-center" />

      <div className="al-head">
        <div>
          <h1 className="al-title">Designer Profile</h1>
          <p className="al-sub">Manage your designer account information.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn" onClick={() => Navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
        </div>
      </div>

      <div className="al-idbanner">
        <label htmlFor="profileUpload" className="image-container" style={{ cursor: "pointer", position: "relative", width: 54, height: 54, borderRadius: 14, overflow: "hidden", flex: "0 0 54px" }}>
          <img
            src={
              profilePreview
                ? profilePreview
                : user?.profile_image
                ? `${process.env.REACT_APP_IMAGE_URL}/uploads/${user.profile_image}`
                : "/assets/img/profileImage.jpg"
            }
            alt="Profile"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div className="overlay">
            <FaEdit style={{ color: "#fff", fontSize: 16 }} />
          </div>
        </label>
        <input type="file" id="profileUpload" style={{ display: "none" }} accept="image/*" onChange={(e) => handleImageChange(e, "profile")} />
        <div>
          <div className="al-idbanner__name">{user?.name || "—"}</div>
          <div className="al-idbanner__sub">{user?.email || "—"} · Click avatar to change photo</div>
        </div>
        <span className="al-pill" style={{ marginLeft: "auto", background: "var(--color-violet-bg)", color: "var(--color-violet-text)" }}>
          Graphic Designer
        </span>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <div className="al-card">
          <div className="al-cardhead">
            <div className="al-cardhead__icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg></div>
            <h3 className="al-cardhead__title">Profile Information</h3>
          </div>
          <div className="al-form">
            <div>
              <label className="al-label">Full Name <span className="al-req">*</span></label>
              <input type="text" className="al-input" value={user.name || ""} onChange={(e) => handleInputChange(e, "name")} />
            </div>
            <div>
              <label className="al-label">Email Address <span className="al-req">*</span></label>
              <input type="email" className="al-input" value={user.email || ""} readOnly style={{ background: "var(--color-surface-2)", cursor: "not-allowed" }} />
            </div>
            <div>
              <label className="al-label">Phone Number <span className="al-req">*</span></label>
              <input type="text" className="al-input" value={user.phone || ""} onChange={(e) => handleInputChange(e, "phone")} />
            </div>
            <div>
              <label className="al-label">PAN Number <span className="al-req">*</span></label>
              <input type="text" className="al-input" style={{ fontFamily: "var(--font-mono)" }} value={user.pan_number || ""} onChange={(e) => handleInputChange(e, "pan_number")} />
            </div>
          </div>
          <div className="al-actionbar">
            <button
              type="button"
              className="al-btn al-btn--primary"
              disabled={!isModified || uploading}
              style={!isModified || uploading ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
              onClick={handleSave}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              {uploading ? "Saving..." : "Update Profile"}
            </button>
          </div>
        </div>
      </form>

      <style>{`
        .image-container:hover .overlay { opacity: 1; }
        .overlay {
          position: absolute; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0, 0, 0, 0.4);
          display: flex; align-items: center; justify-content: center;
          opacity: 0; transition: opacity 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default GDProfileAdd;
